(() => {
  'use strict';

  const formatMoney = (cents, format = '${{amount}}') => {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const value = Number(cents || 0);
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
      if (!Number.isFinite(number)) return '0';
      const parts = (number / 100).toFixed(precision).split('.');
      parts[0] = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return parts[1] ? `${parts[0]}${decimal}${parts[1]}` : parts[0];
    };
    const match = format.match(placeholderRegex);
    if (!match) return formatWithDelimiters(value);
    let result;
    switch (match[1]) {
      case 'amount_no_decimals': result = formatWithDelimiters(value, 0); break;
      case 'amount_with_comma_separator': result = formatWithDelimiters(value, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': result = formatWithDelimiters(value, 0, '.', ','); break;
      default: result = formatWithDelimiters(value, 2); break;
    }
    return format.replace(placeholderRegex, result);
  };

  const showToast = (message) => {
    const toast = document.querySelector('[data-cart-toast]');
    if (!toast) return;
    const messageNode = toast.querySelector('[data-cart-toast-message]');
    if (messageNode) messageNode.textContent = message || '';
    toast.setAttribute('aria-hidden', 'false');
    toast.classList.add('is-visible');
    clearTimeout(window.aevoraToastTimer);
    window.aevoraToastTimer = setTimeout(() => {
      toast.classList.remove('is-visible');
      toast.setAttribute('aria-hidden', 'true');
    }, 4200);
  };

  const refreshCartCount = async () => {
    try {
      const response = await fetch(window.aevoraRoutes.cart, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      document.querySelectorAll('[data-cart-count]').forEach((node) => {
        node.textContent = cart.item_count;
        node.hidden = cart.item_count === 0;
      });
    } catch (error) {
      console.warn('AEVORA: cart count could not be refreshed.', error);
    }
  };

  class ProductForm {
    constructor(form) {
      this.form = form;
      this.root = form.closest('[data-product-root]') || form.parentElement;
      this.variantSelect = form.querySelector('[name="id"]');
      this.submit = form.querySelector('[type="submit"]');
      this.submitText = form.querySelector('[data-submit-text]');
      this.priceNodes = this.root ? this.root.querySelectorAll('[data-product-price]') : [];
      this.compareNodes = this.root ? this.root.querySelectorAll('[data-compare-price]') : [];
      this.variantButtons = this.root ? this.root.querySelectorAll('[data-variant-button]') : [];
      this.moneyFormat = form.dataset.moneyFormat || '${{amount}}';

      this.variantButtons.forEach((button) => {
        button.addEventListener('click', () => this.selectVariant(button.dataset.variantId));
      });
      this.variantSelect?.addEventListener('change', () => this.updateFromSelect());
      form.addEventListener('submit', (event) => this.addToCart(event));
      this.updateFromSelect();
    }

    selectVariant(id) {
      if (!this.variantSelect) return;
      const option = Array.from(this.variantSelect.options).find((item) => item.value === String(id));
      if (!option) return;
      this.variantSelect.value = String(id);
      this.variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }

    updateFromSelect() {
      if (!this.variantSelect) return;
      const option = this.variantSelect.selectedOptions[0];
      if (!option) return;
      const id = option.value;
      const available = option.dataset.available === 'true';
      const price = Number(option.dataset.price || 0);
      const compare = Number(option.dataset.compareAtPrice || 0);

      this.variantButtons.forEach((button) => {
        const active = button.dataset.variantId === id;
        button.classList.toggle('is-selected', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      this.priceNodes.forEach((node) => { node.textContent = formatMoney(price, this.moneyFormat); });
      this.compareNodes.forEach((node) => {
        if (compare > price) {
          node.textContent = formatMoney(compare, this.moneyFormat);
          node.hidden = false;
        } else {
          node.hidden = true;
        }
      });

      if (this.submit) {
        this.submit.disabled = !available;
        if (this.submitText) this.submitText.textContent = available ? window.aevoraStrings.addToCart : window.aevoraStrings.soldOut;
      }

      const mediaId = option.dataset.mediaId;
      if (mediaId && this.root) {
        this.root.querySelectorAll('[data-product-media]').forEach((media) => {
          media.classList.toggle('is-active', media.dataset.mediaId === mediaId);
        });
      }

      const url = new URL(window.location.href);
      if (url.pathname.includes('/products/')) {
        url.searchParams.set('variant', id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    async addToCart(event) {
      event.preventDefault();
      if (!this.submit || this.submit.disabled) return;
      const originalText = this.submitText?.textContent;
      this.submit.classList.add('is-loading');
      this.submit.disabled = true;

      try {
        const response = await fetch(window.aevoraRoutes.cartAdd, {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
          body: new FormData(this.form)
        });
        const item = await response.json();
        if (!response.ok) throw new Error(item.description || item.message || 'Could not add item to cart.');
        showToast(item.product_title || item.title || window.aevoraStrings.added);
        await refreshCartCount();
      } catch (error) {
        alert(error.message);
      } finally {
        this.submit.classList.remove('is-loading');
        this.submit.disabled = false;
        if (this.submitText && originalText) this.submitText.textContent = originalText;
        this.updateFromSelect();
      }
    }
  }

  const init = () => {
    document.querySelectorAll('form[data-product-form]').forEach((form) => new ProductForm(form));

    document.querySelectorAll('[data-choose-variant]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        const targetName = button.dataset.chooseVariant.toLowerCase();
        const root = document.querySelector('[data-product-root]');
        if (root) {
          const match = Array.from(root.querySelectorAll('[data-variant-button]')).find((candidate) =>
            candidate.dataset.variantTitle.toLowerCase().includes(targetName)
          );
          match?.click();
          root.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    });

    document.querySelectorAll('[data-mobile-menu-toggle]').forEach((toggle) => {
      toggle.addEventListener('click', () => {
        const menu = document.querySelector('[data-mobile-menu]');
        if (!menu) return;
        const open = menu.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        document.body.classList.toggle('menu-open', open);
      });
    });

    document.querySelectorAll('[data-mobile-menu] a').forEach((link) => {
      link.addEventListener('click', () => {
        document.querySelector('[data-mobile-menu]')?.classList.remove('is-open');
        document.body.classList.remove('menu-open');
      });
    });

    const revealObserver = 'IntersectionObserver' in window
      ? new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              revealObserver.unobserve(entry.target);
            }
          });
        }, { threshold: 0.12 })
      : null;

    document.querySelectorAll('[data-reveal]').forEach((element) => {
      if (revealObserver) revealObserver.observe(element);
      else element.classList.add('is-visible');
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
